<template>
  <v-container class="registerpage">
    <div class="loginForm">
      <v-card class="mx-auto pa-12 pb-8 rcard" elevation="8" width="400" max-height="610px" rounded="lg">
        
        <!-- Username -->
        <div class="text-subtitle-1 text-medium-emphasis">Username</div>
        <v-text-field
          density="compact"
          placeholder="Username"
          variant="outlined"
          v-model="username"
          :error-messages="usernameErrors"
          @input="validateForm"
        ></v-text-field>

        <!-- Email -->
        <div class="text-subtitle-1 text-medium-emphasis">Email</div>
        <v-text-field
          density="compact"
          placeholder="Email address"
          prepend-inner-icon="mdi-email-outline"
          variant="outlined"
          v-model="email"
          :error-messages="emailErrors"
          @input="validateForm"
        ></v-text-field>
  
        <!-- Password -->
        <div class="text-subtitle-1 text-medium-emphasis">Password</div>
        <v-text-field
          :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
          :type="visible ? 'text' : 'password'"
          density="compact"
          v-model="password"
          placeholder="Enter your password"
          prepend-inner-icon="mdi-lock-outline"
          variant="outlined"
          @click:append-inner="visible = !visible"
          :error-messages="passwordErrors"
          @input="validateForm"
        ></v-text-field>

        <!-- Captcha -->
        <div class="text-subtitle-1 text-medium-emphasis">Captcha</div>
        <vue-hcaptcha 
          theme="dark" 
          sitekey="293e4b3c-cda0-4392-af0c-e8cac07e1762"
          @verify="onVerify"
          @expire="onExpire"
        ></vue-hcaptcha>

        <br>
        <span style="color: grey;">By clicking the Register button<br> and creating an account, you agree to the site's <a href="terms" class="ptexte" target="_blank">Terms & conditions</a></span>
        <br>
        <v-btn block class="mb-8" color="blue" size="large" variant="tonal" @click="register" :disabled="isFormInvalid">
          Register
        </v-btn>

      </v-card>
    </div>
  </v-container>
</template>

<script>
import { useToast } from "vue-toastification";
import { mapGetters } from 'vuex';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';

export default {
  data: () => ({
    username: '',
    email: '',
    password: '',
    visible: false,
    captchaSiteKey: "293e4b3c-cda0-4392-af0c-e8cac07e1762",
    captcha_payload: null,
    usernameErrors: [],
    emailErrors: [],
    passwordErrors: [],
  }),
  computed: {
    ...mapGetters(['backend']),
    isFormInvalid() {
      return this.usernameErrors.length || this.emailErrors.length || this.passwordErrors.length || !this.captcha_payload;
    }
  },
  components: {
    VueHcaptcha
  },
 
  methods: {
    onVerify(token) {
      this.captcha_payload = token;
    },
    onExpire() {
      this.captcha_payload = null;
      this.resetCaptcha();
    },
    validateForm() {
      // Username validation
      if (this.username.length < 4 || this.username.length > 15) {
        this.usernameErrors = ['Username must be between 4 and 15 characters.'];
      } else {
        this.usernameErrors = [];
      }

      // Email validation
      const emailDomain = this.email.split('@')[1];
      const allowedEmailDomains = [    
      'gmail.com',
      'outlook.com',
      'yahoo.com',
      'hotmail.com',
      'protonmail.com',
      'proton.me',
      'icloud.com',
      'aol.com',
      'zoho.com',
      'gmx.com',
      'yandex.com',
      'mail.com',
      'tutanota.com',
      'fastmail.com',
      'posteo.net',
      'hushmail.com',
      'me.com',
      'msn.com',
      'live.com',
      'pm.me', // Protonmail alias
      'runbox.com',
      'mailfence.com',
      'laposte.net',
      'orange.fr',
      'comcast.net',
      'verizon.net',
      'att.net',
      'sbcglobal.net',
      'btinternet.com',
      'virginmedia.com',
      'cox.net',
      'earthlink.net',
      'bluewin.ch',
      'neuf.fr',
      'wanadoo.fr',
      'seznam.cz',
      'free.fr',
      't-online.de',
      'web.de',
      'talktalk.net',
      'ntlworld.com',
      'shaw.ca',
      'rogers.com',
      'sympatico.ca',
      'bell.net',
      'charter.net',
      'sky.com',
      'ntlworld.com',
      'tele2.nl',
      'xs4all.nl']; 
      if (!this.email.includes('@') || !allowedEmailDomains.includes(emailDomain)) {
        this.emailErrors = ['Invalid or unapproved email domain.'];
      } else {
        this.emailErrors = [];
      }

      // Password validation
      if (this.password.length < 8 || this.password.length > 50) {
        this.passwordErrors = ['Password must be between 8 and 50 characters.'];
      } else {
        this.passwordErrors = [];
      }
    },

    async register(){
      const toast = useToast();
      try {
        const response = await this.$axios.post(`${this.backend}/api/user/register`, {
          username: this.username,
          email: this.email,
          password: this.password,
          hcaptcha_payload: this.captcha_payload,
        });

        const authToken = response.data.authToken;
        const userdata = response.data.userdata;
        localStorage.setItem('Authorization', "Bearer " + authToken);
        document.cookie = `Authorization=${authToken}; path=/; domain=.xsint-intelligence.com; secure; expires=Fri, 31 Dec 9999 23:59:59 GMT`;
        localStorage.setItem('userdata', JSON.stringify(userdata));
        window.location.href = '/email-verification';
      } catch (err) {
        this.captcha_payload = null;
        this.resetCaptcha();
        toast.error((err.response.data.error).toString());
      }
    },

    resetCaptcha() {
      const container = document.getElementById('hcaptcha-container');
      if (container) {
        container.innerHTML = '';
        const script = document.createElement('script');
        script.src = `https://hcaptcha.com/1/api.js?onload=onloadCallback&render=explicit`;
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
        
        script.onload = () => {
          window.hcaptcha.render(container, {
            sitekey: this.captchaSiteKey,
            theme: "dark",
            callback: this.onVerify,
            'expired-callback': this.onExpire
          });
        };
      }
    }
  }
}
</script>

<style scoped>
.registerpage {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
}

.rcard {
  background-color: rgba(34, 34, 34, 0.459);
}

.loginForm {
  margin-top: 70px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
}

.ptexte {
  text-decoration: none;
  color: #0099ff;
}
</style>
